import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, Loader2, BarChart2, Zap,
  ArrowRight, TrendingUp, TrendingDown, Minus,
  Image, Code2, Wrench, RefreshCw, Sparkles,
  ChevronDown, ChevronUp, Rocket, Clock, Info,
} from "lucide-react";

/* ─── Helpers ─────────────────────────────────────────── */
function scoreColor(s) {
  if (s >= 90) return "#059669";
  if (s >= 50) return "#D97706";
  return "#DC2626";
}

function DeltaBadge({ value, invert = false, unit = "نقطة" }) {
  // invert=true: negative delta is GOOD (e.g. timing values)
  const improved = invert ? value < 0 : value > 0;
  const neutral = value === 0;
  const abs = Math.abs(value);

  if (neutral || !value) return <span className="text-xs text-slate-400">—</span>;

  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full
      ${improved ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-600 border border-red-200"}`}>
      {improved ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {improved ? "+" : "-"}{abs > 1000 ? (abs / 1000).toFixed(1) + "s" : abs}{unit}
    </span>
  );
}

/* ─── Mini Gauge ─────────────────────────────────────── */
function MiniGauge({ score, label, size = 100 }) {
  const r = (size / 2) - 8;
  const c = 2 * Math.PI * r;
  const col = scoreColor(score);
  const offset = c - (score / 100) * c;
  const cx = size / 2;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="#F1F5F9" strokeWidth={6} />
        <circle cx={cx} cy={cx} r={r} fill="none" stroke={col} strokeWidth={6}
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cx})`}
          style={{ transition: "stroke-dashoffset 1.4s cubic-bezier(.4,0,.2,1)" }} />
        <text x={cx} y={cx - 4} textAnchor="middle" fill={col} fontSize={size > 80 ? 22 : 16} fontWeight="bold" fontFamily="monospace">{score}</text>
        <text x={cx} y={cx + 10} textAnchor="middle" fill="#94A3B8" fontSize={8}>/100</text>
      </svg>
      <span className="text-[10px] text-slate-500 font-medium">{label}</span>
    </div>
  );
}

/* ─── Before/After Comparison Card ─────────────────── */
function BeforeAfterComparison({ baseline, post, delta }) {
  const mobileDelta = delta?.mobile_perf ?? 0;
  const improved = mobileDelta > 0;

  return (
    <div>
      {/* Hero Score Comparison */}
      <div className="flex items-center justify-center gap-4 sm:gap-8 py-6 px-4 rounded-2xl bg-gradient-to-b from-slate-50 to-white border border-slate-100 mb-5">
        {/* Before */}
        <div className="text-center">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">قبل التحسين</div>
          <MiniGauge score={baseline.mobile_perf} label="جوال" size={100} />
        </div>

        {/* Delta */}
        <div className="flex flex-col items-center gap-2 px-4">
          <ArrowRight className="w-5 h-5 text-slate-300" />
          <div className={`text-3xl font-black ${improved ? "text-emerald-600" : "text-red-500"}`}>
            {mobileDelta > 0 ? "+" : ""}{mobileDelta}
          </div>
          <div className="text-[10px] text-slate-400">نقطة الأداء</div>
          {improved && (
            <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
              تحسّن!
            </span>
          )}
        </div>

        {/* After */}
        <div className="text-center">
          <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider mb-3">بعد التحسين</div>
          <MiniGauge score={post.mobile_perf} label="جوال" size={100} />
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: "أداء سطح المكتب", before: baseline.desktop_perf, after: post.desktop_perf, delta: delta?.desktop_perf, invert: false, unit: "نقطة" },
          { label: "SEO", before: baseline.seo, after: post.seo, delta: delta?.seo, invert: false, unit: "نقطة" },
          { label: "LCP", before: baseline.lcp_ms ? (baseline.lcp_ms / 1000).toFixed(1) + "s" : "—", after: post.lcp_ms ? (post.lcp_ms / 1000).toFixed(1) + "s" : "—", delta: delta?.lcp_ms, invert: true, unit: "ms" },
          { label: "TBT", before: baseline.tbt_ms ? baseline.tbt_ms + "ms" : "—", after: post.tbt_ms ? post.tbt_ms + "ms" : "—", delta: delta?.tbt_ms, invert: true, unit: "ms" },
        ].map((m) => (
          <div key={m.label} className="bg-white rounded-xl border border-slate-100 p-3 shadow-sm text-center">
            <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">{m.label}</div>
            <div className="flex items-center justify-center gap-2 mb-1.5">
              <span className="text-xs text-slate-400 line-through">{m.before}</span>
              <ArrowRight className="w-3 h-3 text-slate-300" />
              <span className="text-xs font-bold" style={{ color: scoreColor(m.after && typeof m.after === "number" ? m.after : 50) }}>
                {m.after}
              </span>
            </div>
            <DeltaBadge value={m.delta} invert={m.invert} unit={m.unit} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Optimization Checklist Item ─────────────────── */
const OPTS = [
  { type: "bulk_images",  title: "ضغط صور المنتجات (WebP)",         icon: Image,   auto: true,  desc: "تم تطبيقه تلقائياً عبر Salla API" },
  { type: "full_script",  title: "تركيب حزمة الأداء الكاملة",       icon: Code2,   auto: false, desc: "تركيب الكود في: سلة > التطبيقات > الكود المخصص" },
  { type: "lazy_script",  title: "تفعيل Lazy Loading للصور",         icon: Zap,     auto: false, desc: "تركيب سكربت التحميل الكسول في سلة" },
  { type: "reduce_prods", title: "تقليل منتجات الصفحة الرئيسية",    icon: Wrench,  auto: false, desc: "تعديل إعدادات الثيم في لوحة سلة" },
  { type: "remove_apps",  title: "حذف التطبيقات غير المستخدمة",     icon: Wrench,  auto: false, desc: "سلة > التطبيقات > حذف غير النشط" },
];

function OptimizationCheckItem({ opt, appliedTypes, onMark }) {
  const isApplied = appliedTypes.includes(opt.type);
  const OIcon = opt.icon;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border transition-all
      ${isApplied ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-100"}`}
      data-testid={`opt-item-${opt.type}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
        ${isApplied ? "bg-emerald-100" : "bg-slate-100"}`}>
        <OIcon className="w-4 h-4" style={{ color: isApplied ? "#059669" : "#64748B", width: 14, height: 14 }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-bold ${isApplied ? "text-emerald-800 line-through" : "text-slate-800"}`}>{opt.title}</div>
        <div className="text-[10px] text-slate-400 mt-0.5">{opt.desc}</div>
      </div>
      {isApplied ? (
        <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
      ) : (
        <button onClick={() => onMark(opt)}
          data-testid={`mark-applied-${opt.type}`}
          className="text-[10px] font-bold px-2.5 py-1.5 rounded-lg bg-[#004EEB] text-white hover:bg-[#003BBB] transition-colors shrink-0 whitespace-nowrap">
          {opt.auto ? "طبّق" : "تم التطبيق"}
        </button>
      )}
    </div>
  );
}

/* ─── Step Indicator ─────────────────────────────── */
function StepIndicator({ steps, current }) {
  return (
    <div className="flex items-center gap-0 mb-6 overflow-x-auto pb-1">
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        const SIcon = s.icon;
        return (
          <div key={i} className="flex items-center shrink-0">
            <div className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-all
              ${done ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                active ? "bg-[#004EEB] text-white" :
                "bg-slate-50 text-slate-400 border border-slate-100"}`}>
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <SIcon className="w-3.5 h-3.5" />}
              {s.label}
            </div>
            {i < steps.length - 1 && (
              <ArrowRight className={`w-3.5 h-3.5 mx-1 shrink-0 ${done ? "text-emerald-400" : "text-slate-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ═════════════════════════════════════════════════════
   MAIN EXPORT: Optimization Loop
═════════════════════════════════════════════════════ */
export default function OptimizationLoop({ analysis }) {
  const [session, setSession] = useState(null);
  const [creating, setCreating] = useState(false);
  const [remeasuring, setRemeasuring] = useState(false);
  const [loadStep, setLoadStep] = useState(0);
  const [expanded, setExpanded] = useState(true);

  const STEPS = [
    { label: "القياس الأولي", icon: BarChart2 },
    { label: "تطبيق التحسينات", icon: Zap },
    { label: "إعادة القياس", icon: RefreshCw },
    { label: "النتائج", icon: Sparkles },
  ];

  const currentStep = session
    ? session.status === "completed" ? 3
      : session.status === "remeasuring" ? 2
      : 1
    : 0;

  const appliedTypes = (session?.applied_optimizations || []).map((o) => o.type);

  // Load existing session for this analysis if any
  useEffect(() => {
    if (!analysis?.id) return;
    api.get("/optimizer/sessions")
      .then(({ data }) => {
        const existing = data.find((s) => s.baseline_id === analysis.id);
        if (existing) setSession(existing);
      })
      .catch(() => {});
  }, [analysis?.id]);

  const startLoop = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/optimizer/sessions", { analysis_id: analysis.id });
      setSession(data);
      toast.success("تم إنشاء حلقة التحسين! طبّق التحسينات ثم أعد القياس.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إنشاء الجلسة");
    } finally { setCreating(false); }
  };

  const markApplied = async (opt) => {
    if (!session) return;
    try {
      const { data } = await api.post(`/optimizer/sessions/${session.id}/mark-applied`, {
        type: opt.type, title: opt.title, auto: opt.auto,
      });
      setSession(data);
      toast.success(`تم تسجيل: ${opt.title}`);
    } catch { toast.error("فشل التسجيل"); }
  };

  const remeasure = async () => {
    if (!session) return;
    setRemeasuring(true); setLoadStep(0);
    const timer = setInterval(() => setLoadStep((p) => Math.min(p + 1, 3)), 5000);
    try {
      const { data } = await api.post(`/optimizer/sessions/${session.id}/remeasure`);
      setSession(data.session);
      toast.success("اكتملت إعادة القياس! شاهد النتائج.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إعادة القياس");
    } finally { clearInterval(timer); setRemeasuring(false); }
  };

  if (!analysis) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm mt-5"
      data-testid="optimization-loop-panel">

      {/* Header */}
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-5 py-4 bg-gradient-to-l from-[#004EEB]/5 to-white border-b border-slate-100 hover:bg-[var(--bg-card-hover)] transition-colors text-right">
        <div className="w-9 h-9 rounded-xl bg-[#004EEB]/8 border border-[#004EEB]/15 flex items-center justify-center shrink-0">
          <RefreshCw className="w-4 h-4 text-[#004EEB]" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-black text-slate-900">حلقة التحسين المغلقة</div>
          <div className="text-xs text-slate-400 mt-0.5">قيس → حسّن → قيس مجدداً → تحقق من التأثير الحقيقي</div>
        </div>
        {session?.status === "completed" && (
          <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
            اكتملت
          </span>
        )}
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>

      {expanded && (
        <div className="p-5">
          {/* Stepper */}
          <StepIndicator steps={STEPS} current={currentStep} />

          {/* ── Start prompt ── */}
          {!session && (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-2xl bg-[#004EEB]/6 border border-[#004EEB]/10 flex items-center justify-center mx-auto mb-3">
                <Rocket className="w-7 h-7 text-[#004EEB]/50" />
              </div>
              <div className="text-sm font-bold text-slate-700 mb-1">ابدأ حلقة التحسين</div>
              <div className="text-xs text-slate-400 max-w-xs mx-auto mb-4 leading-relaxed">
                سيتم حفظ نقاط أدائك الحالية كخط أساس، ثم بعد تطبيق التحسينات تُعاد المقارنة تلقائياً.
              </div>
              <button onClick={startLoop} disabled={creating} data-testid="start-loop-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-[#004EEB] text-white text-sm font-bold rounded-xl mx-auto hover:bg-[#003BBB] disabled:opacity-60 transition-all hover:shadow-lg hover:shadow-blue-200">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                ابدأ الحلقة
              </button>
              <div className="mt-3 text-[10px] text-slate-400">
                الخط الأساس: أداء الجوال {analysis?.mobile?.scores?.performance ?? "—"}/100
              </div>
            </div>
          )}

          {/* ── Baseline shown ── */}
          {session && currentStep >= 0 && (
            <div className="bg-slate-50 rounded-xl p-4 mb-5 border border-slate-100">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-bold text-slate-700">الخط الأساس (القياس الأولي)</span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: "جوال", val: session.baseline_scores?.mobile_perf },
                  { label: "سطح المكتب", val: session.baseline_scores?.desktop_perf },
                  { label: "SEO", val: session.baseline_scores?.seo },
                  { label: "وصول", val: session.baseline_scores?.accessibility },
                ].map((m) => (
                  <div key={m.label} className="text-center bg-white rounded-lg p-2 border border-slate-100">
                    <div className="text-base font-bold font-mono" style={{ color: scoreColor(m.val || 0) }}>{m.val ?? "—"}</div>
                    <div className="text-[9px] text-slate-400 mt-0.5">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Optimization Checklist ── */}
          {session && currentStep <= 1 && (
            <div className="mb-5">
              <div className="text-xs font-bold text-slate-600 mb-3">
                طبّق التحسينات ثم اضغط "تم التطبيق" لكل واحد:
                <span className="ms-2 text-[#004EEB]">{appliedTypes.length}/{OPTS.length} مطبّق</span>
              </div>
              <div className="space-y-2">
                {OPTS.map((opt) => (
                  <OptimizationCheckItem key={opt.type} opt={opt} appliedTypes={appliedTypes} onMark={markApplied} />
                ))}
              </div>
              {appliedTypes.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-100 text-xs text-blue-700">
                  طبّقت {appliedTypes.length} تحسين. كلما طبّقت أكثر، كلما كانت النتيجة أوضح.
                </div>
              )}
            </div>
          )}

          {/* ── Re-measure Button ── */}
          {session && session.status !== "completed" && (
            <div className="border-t border-slate-100 pt-4">
              {appliedTypes.length === 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-xl mb-3">
                  <span className="text-amber-500 text-sm shrink-0">⚠</span>
                  <div className="text-xs text-amber-700">
                    لم تُطبَّق أي تحسينات بعد. يمكنك إعادة القياس الآن لكن النتائج قد لا تختلف.
                  </div>
                </div>
              )}
              <button onClick={remeasure} disabled={remeasuring} data-testid="remeasure-btn"
                className="flex items-center gap-2 px-5 py-3 bg-[#004EEB] text-white text-sm font-bold rounded-xl w-full justify-center disabled:opacity-60 hover:bg-[#003BBB] transition-all hover:shadow-lg hover:shadow-blue-200">
                {remeasuring
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> {["جاري التحليل...", "تحليل الجوال...", "تحليل سطح المكتب...", "حساب التحسّن..."][loadStep]}</>
                  : <><RefreshCw className="w-4 h-4" /> قيّس الآن بعد التحسينات</>}
              </button>
            </div>
          )}

          {/* ── Results: Before / After ── */}
          {session?.status === "completed" && session.delta && (
            <div data-testid="before-after-results">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-violet-500" />
                <span className="text-sm font-bold text-slate-800">نتائج التحسين — قبل وبعد</span>
              </div>
              <BeforeAfterComparison
                baseline={session.baseline_scores}
                post={session.post_scores}
                delta={session.delta}
              />

              {/* Estimated mode hint */}
              {Object.values(session.delta || {}).every((v) => v === 0) && (
                <div className="mt-4 flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-xl">
                  <Info className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                  <div className="text-[11px] text-blue-700 leading-relaxed">
                    الفرق صفر — هذا متوقع في وضع التحليل التقريبي (PageSpeed API غير مفعّل). فعّل PageSpeed API للحصول على فروقات حقيقية بعد تطبيق التحسينات.
                  </div>
                </div>
              )}

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => setSession(null)}
                  data-testid="reset-loop-btn"
                  className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg hover:bg-[var(--bg-card-hover)] transition-colors">
                  <RefreshCw className="w-3 h-3" /> جولة تحسين جديدة
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
